import { useEffect, useState } from "react";
import { useSession } from "@/auth/SessionProvider";
import {
  resolveCrmLeadFieldIconDisplayUrl,
  resolveCrmLeadFieldIconOrgId,
} from "@/lib/crmLeadFieldIcon";
import type { CrmContactSocialPlatform } from "@/lib/crmContactChannels";
import { formatCrmSocialPlatformLabel } from "@/lib/crmContactChannels";

function socialPlatformBadge(platform: CrmContactSocialPlatform): string {
  if (platform === "linkedin") return "in";
  if (platform === "facebook") return "f";
  if (platform === "twitter") return "X";
  if (platform === "instagram") return "IG";
  if (platform === "youtube") return "YT";
  if (platform === "github") return "GH";
  return "www";
}

type CrmSocialPlatformIconProps = {
  platform: CrmContactSocialPlatform;
  iconUrl?: string | null;
  orgId?: string | null;
  className?: string;
  title?: string;
};

export function CrmSocialPlatformIcon({
  platform,
  iconUrl,
  orgId: orgIdProp,
  className = "inline-flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-300 bg-white dark:border-white/20 dark:bg-black/20",
  title,
}: CrmSocialPlatformIconProps) {
  const session = useSession();
  const orgId = resolveCrmLeadFieldIconOrgId(
    orgIdProp?.trim() || session.data?.user?.orgId?.trim() || null,
    iconUrl,
  );
  const [displaySrc, setDisplaySrc] = useState<string | null>(null);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const raw = (iconUrl || "").trim();
    setImageFailed(false);
    if (!raw) {
      setDisplaySrc(null);
      return;
    }
    void resolveCrmLeadFieldIconDisplayUrl(orgId, raw).then((url) => {
      if (!cancelled) setDisplaySrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [iconUrl, orgId]);

  const label = title || formatCrmSocialPlatformLabel(platform);

  if (displaySrc && !imageFailed) {
    return (
      <span className={className} title={label}>
        <img
          src={displaySrc}
          alt=""
          className="h-full w-full object-contain p-0.5"
          onError={() => setImageFailed(true)}
        />
      </span>
    );
  }

  return (
    <span
      className={`${className} text-[10px] font-bold uppercase text-gray-600 dark:text-gray-300`}
      title={label}
      aria-hidden
    >
      {socialPlatformBadge(platform)}
    </span>
  );
}
